import * as Sentry from "@sentry/capacitor";
import type React from "react";
import { Component } from "react";
import { interfaceTranslator } from "@/i18n/translate";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  hasError: boolean;
}

/**
 * The raw error and component stack. Development builds only: a host cannot
 * act on either, and Sentry already receives both from `componentDidCatch`.
 */
function ErrorDetails({
  error,
  errorInfo,
}: Pick<State, "error" | "errorInfo">) {
  return (
    <>
      {error !== null && (
        <div className="rounded bg-destructive/10 p-3 font-mono text-destructive text-xs">
          <p className="font-semibold">{error.name}</p>
          <p>{error.message}</p>
        </div>
      )}

      {errorInfo !== null && (
        <details className="rounded bg-muted p-3">
          <summary className="cursor-pointer font-medium text-muted-foreground text-xs">
            Component stack
          </summary>
          <pre className="mt-2 max-h-40 overflow-auto text-muted-foreground text-xs">
            {errorInfo.componentStack}
          </pre>
        </details>
      )}
    </>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, errorInfo: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error, errorInfo: null, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null, hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const t = interfaceTranslator("shell.errorBoundary");
      return (
        <div className="flex h-dvh flex-col items-center justify-center bg-background p-6 text-foreground">
          <div className="w-full max-w-md space-y-4 rounded-lg border border-destructive/30 bg-card p-6 shadow-lg">
            <h1 className="font-semibold text-destructive text-xl">
              {t("title")}
            </h1>
            <p className="text-muted-foreground text-sm">{t("description")}</p>

            {import.meta.env.DEV ? (
              <ErrorDetails
                error={this.state.error}
                errorInfo={this.state.errorInfo}
              />
            ) : null}

            <button
              className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm hover:bg-primary/90"
              onClick={this.handleReset}
              type="button"
            >
              {t("reload")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
